import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";
import {
  DEFAULT_CONSENT,
  readConsent,
  saveConsent,
  acceptAll,
  rejectAll,
  applyConsent,
} from "../consent";
import { ShieldCheck } from "lucide-react";

export default function CookieBanner() {
  const { lang, t } = useI18n();
  const base = `/${lang}`;
  const [visible, setVisible] = useState(false);
  const [manage, setManage] = useState(false);
  const [choices, setChoices] = useState(DEFAULT_CONSENT);

  useEffect(() => {
    const stored = readConsent();
    if (stored) {
      applyConsent(stored);
      setChoices(stored);
    } else {
      setVisible(true);
    }
    const openHandler = () => {
      const s = readConsent() || DEFAULT_CONSENT;
      setChoices(s);
      setVisible(true);
      setManage(true);
    };
    window.addEventListener("geolitio:open-cookie-preferences", openHandler);
    return () => window.removeEventListener("geolitio:open-cookie-preferences", openHandler);
  }, []);

  if (!visible) return null;

  const onAccept = () => {
    acceptAll();
    setVisible(false);
  };
  const onReject = () => {
    rejectAll();
    setVisible(false);
  };
  const onSave = () => {
    saveConsent(choices);
    setVisible(false);
  };

  const toggle = (key) => setChoices((c) => ({ ...c, [key]: !c[key] }));

  return (
    <div className="cookie-banner" role="dialog" aria-live="polite" aria-label={t("cookie.title")} data-testid="cookie-banner">
      <div className="cookie-banner__panel">
        <div className="cookie-banner__header">
          <div className="cookie-banner__icon" aria-hidden="true"><ShieldCheck size={22} /></div>
          <div>
            <h3>{t("cookie.title")}</h3>
            <p>{t("cookie.text")}</p>
            <Link className="cookie-banner__policy" to={`${base}/cookies`} data-testid="cookie-banner-policy">
              {t("cookie.policyLink")}
            </Link>
          </div>
        </div>

        {manage && (
          <div className="cookie-banner__manage">
            {["necessary", "functional", "analytics", "ads"].map((key) => (
              <label key={key} className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={choices[key]}
                  onChange={() => toggle(key)}
                  disabled={key === "necessary"}
                  aria-label={t(`cookie.categories.${key}.title`)}
                  data-testid={`cookie-toggle-${key}`}
                />
                <span className="cookie-toggle__text">
                  <strong>{t(`cookie.categories.${key}.title`)}</strong>
                  <span>{t(`cookie.categories.${key}.desc`)}</span>
                </span>
              </label>
            ))}
          </div>
        )}

        <div className="cookie-banner__actions">
          {!manage ? (
            <>
              <button type="button" className="btn btn--ghost" onClick={() => setManage(true)} data-testid="cookie-manage">
                {t("cookie.manage")}
              </button>
              <button type="button" className="btn btn--ghost" onClick={onReject} data-testid="cookie-reject">
                {t("cookie.reject")}
              </button>
              <button type="button" className="btn btn--primary" onClick={onAccept} data-testid="cookie-accept">
                {t("cookie.accept")}
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn--ghost" onClick={() => setManage(false)} data-testid="cookie-back">
                {t("cookie.back")}
              </button>
              <button type="button" className="btn btn--ghost" onClick={onReject} data-testid="cookie-reject">
                {t("cookie.reject")}
              </button>
              <button type="button" className="btn btn--primary" onClick={onSave} data-testid="cookie-save">
                {t("cookie.save")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
